import cloneDeep from 'clone-deep'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { Modal, Select } from 'stories/components'
import { getPrice2decimal } from 'utils'

import type { IBudgetData } from './types'
import { type UnmatchedBudgetItem,buildUnmatchedBudgetPlan, parseUnmatchedSelection } from './unmatchedBudgetPlan'

export type { UnmatchedBudgetItem }

export type UnmatchedBudgetAssignment = {
  sectionValue: string
  lineKey: string | null
  title: string | null
  rehabCostBorrowerEstimated: number | null
  rehabCostAlreadyPrepaid: number | null
  costType: string | null
  description: string | null
  /** Set when the target section does not exist yet and must be created under this label. */
  newSectionLabel?: string | null
}

type Props = {
  unmatched: UnmatchedBudgetItem[]
  budget: IBudgetData
  /** Roster keys already filled by matched extract (`sectionValue::lineKey`) */
  occupiedLineKeys?: string[]
  onClose: (saved: boolean) => void
  onAssign: (assignments: UnmatchedBudgetAssignment[]) => Promise<void> | void
}

export const AddUnmatchedBudgetItemsModal = ({
  unmatched,
  budget,
  occupiedLineKeys = [],
  onClose,
  onAssign,
}: Props) => {
  const [selections, setSelections] = useState<Record<number, string>>({})
  const [loading, setLoading] = useState(false)

  const { options, newSectionLabels, selections: suggested } = useMemo(
    () => buildUnmatchedBudgetPlan(unmatched, budget.items || [], occupiedLineKeys),
    [unmatched, budget, occupiedLineKeys.join('|')],
  )

  useEffect(() => {
    setSelections(suggested)
  }, [suggested])

  const selectedValues = Object.values(selections).filter(Boolean)
  const lineSelections = selectedValues.filter((v) => v.startsWith('line:'))
  const hasDuplicateLineSelection = lineSelections.length !== new Set(lineSelections).size
  const assignedCount = unmatched.filter((_, idx) => !!selections[idx]).length
  const skippedCount = unmatched.length - assignedCount

  const onSelect = (idx: number, value: string) => {
    const temp = cloneDeep(selections)
    if (!value) delete temp[idx]
    else temp[idx] = value
    setSelections(temp)
  }

  const onModalClose = async (save: boolean) => {
    if (!save) {
      onClose(false)
      return
    }
    if (!assignedCount) {
      if (!Object.keys(options).length) {
        toast('No budget sections available to assign unmatched items.', { type: 'warning' })
        return
      }
      toast('Select a budget line or section for at least one item.', { type: 'warning' })
      return
    }
    if (hasDuplicateLineSelection) {
      toast('Each unmatched item must map to a different existing budget line.', { type: 'warning' })
      return
    }

    setLoading(true)
    try {
      // Rows left unassigned are skipped, not blocked. A hundred-line form always has a handful of
      // rows with nowhere sensible to go, and refusing the whole import over them helped no one.
      const assignments: UnmatchedBudgetAssignment[] = []
      unmatched.forEach((item, idx) => {
        const parsed = parseUnmatchedSelection(selections[idx])
        if (!parsed) return
        assignments.push({
          sectionValue: parsed.sectionValue,
          lineKey: parsed.lineKey,
          title: item.title,
          rehabCostBorrowerEstimated: item.rehabCostBorrowerEstimated,
          rehabCostAlreadyPrepaid: item.rehabCostAlreadyPrepaid,
          costType: item.costType,
          description: item.description,
          newSectionLabel: parsed.newSectionLabel,
        })
      })
      await onAssign(assignments)
      onClose(true)
    } catch {
      toast('Failed to assign unmatched budget items.', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      title="Assign unmatched budget items"
      loading={loading}
      isOpen
      onClose={() => onModalClose(false)}
      onOk={() => onModalClose(true)}
      disabled={!assignedCount || hasDuplicateLineSelection}
      titleOkay={assignedCount ? `Assign ${assignedCount} Item${assignedCount === 1 ? '' : 's'}` : 'Assign Items'}
    >
      <div className="w-[720px] max-w-full space-y-3">
        <p className="text-sm text-gray-600">
          Each item from the document is mapped to an existing budget line, or added as a new item under a section (same
          as “Add Item”). Change anything that looks wrong, and clear a row to leave it out.
        </p>
        {skippedCount > 0 && (
          <p className="text-sm text-gray-600">
            {skippedCount} item{skippedCount === 1 ? '' : 's'} left unassigned will be skipped.
          </p>
        )}
        {newSectionLabels.length > 0 && (
          <p className="text-sm text-gray-600">
            The document is organised into sections this budget does not have. They can be added as new sections:{' '}
            {newSectionLabels.join(', ')}.
          </p>
        )}
        {occupiedLineKeys.length > 0 && (
          <p className="text-sm text-amber-700">
            Lines already filled by matched extract are excluded to avoid overwriting them.
          </p>
        )}
        {hasDuplicateLineSelection && (
          <p className="text-sm text-amber-700">Each unmatched item must use a different existing budget line.</p>
        )}
        <div className="overflow-auto border rounded max-h-[60vh]">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left sticky top-0">
              <tr>
                <th className="p-2">From document</th>
                <th className="p-2 text-right">Estimated</th>
                <th className="p-2 min-w-[280px]">Assign to</th>
              </tr>
            </thead>
            <tbody>
              {unmatched.map((item, idx) => (
                <tr key={idx} className="border-t align-top">
                  <td className="p-2">
                    <div className="font-medium">{item.title || 'Unnamed item'}</div>
                    {item.section && <div className="text-xs text-gray-500 mt-0.5">under “{item.section}”</div>}
                    {item.description && <div className="text-xs text-gray-500 mt-0.5">{item.description}</div>}
                    {item.costType && <div className="text-xs text-gray-500">{item.costType}</div>}
                  </td>
                  <td className="p-2 text-right whitespace-nowrap">
                    {item.rehabCostBorrowerEstimated != null
                      ? getPrice2decimal(item.rehabCostBorrowerEstimated, false, true)
                      : '—'}
                  </td>
                  <td className="p-2">
                    <Select
                      id={`unmatched-budget-${idx}`}
                      title=""
                      value={selections[idx] || ''}
                      options={options}
                      hasDefaultOption
                      defaultOptionText="- Select line or section -"
                      onChange={(v) => onSelect(idx, v)}
                      className="mb-0"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Modal>
  )
}
